import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { HiChevronRight } from "react-icons/hi";
import { resolveProfileImageUrl, resolveImageUrl } from "../util/profileImage";

export const LIKES_BAR_HEIGHT = "88px";
const AVATAR_SIZE = "48px";
const AVATAR_OVERLAP = "-10px";
const SECTION_BLUR = "9px";

function LikeAvatar({ notif, index = 0, stackSize = 1, onClick }) {
  const ring = useColorModeValue("brand.300", "brand.400");
  const placeholderBg = useColorModeValue("gray.200", "whiteAlpha.150");
  const galleryPrimary =
    Array.isArray(notif.images) && notif.images.length > 0
      ? resolveImageUrl(notif.images[0])
      : Array.isArray(notif.user?.images) && notif.user.images.length > 0
        ? resolveImageUrl(notif.user.images[0])
        : null;
  const imageUrl =
    galleryPrimary ||
    resolveProfileImageUrl({
      profileImageUri: notif.user.profileImageUri,
      images: notif.images ?? notif.user.images,
      firstName: notif.user.firstName,
      lastName: notif.user.lastName,
    });
  const showImage = imageUrl && !imageUrl.includes("ui-avatars.com");
  const firstName = notif.user.firstName || "?";

  const photo = showImage ? (
    <Box as="img" src={imageUrl} alt="" w="100%" h="100%" objectFit="cover" display="block" />
  ) : (
    <Avatar w="100%" h="100%" name={firstName} bg="brand.700" />
  );

  return (
    <Box
      as="button"
      type="button"
      boxSize={AVATAR_SIZE}
      borderRadius="full"
      flexShrink={0}
      ml={index === 0 ? 0 : AVATAR_OVERLAP}
      zIndex={index + 1}
      position="relative"
      cursor="pointer"
      onClick={onClick}
      bg="transparent"
      p={0}
      _hover={{ transform: "scale(1.05)", zIndex: stackSize + 2 }}
      transition="transform 0.15s"
    >
      <Box
        boxSize="100%"
        borderRadius="full"
        borderWidth="2px"
        borderColor={ring}
        overflow="hidden"
        bg={placeholderBg}
        pointerEvents="none"
        boxShadow="0 2px 8px rgba(0,0,0,0.22)"
      >
        {photo}
      </Box>
    </Box>
  );
}

export default function LikesCountBar({
  likesNotifications = [],
  blurred = false,
  onUpgrade,
  showClearHint = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const likeCount = likesNotifications.length;

  const shellBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const countColor = useColorModeValue("gray.900", "white");
  const labelColor = useColorModeValue("gray.600", "whiteAlpha.700");
  const muted = useColorModeValue("gray.500", "whiteAlpha.600");
  const accent = useColorModeValue("brand.600", "brand.300");
  const countPanelBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const overlayBg = useColorModeValue("rgba(255,255,255,0.35)", "rgba(6,6,8,0.35)");
  const viewAllHover = useColorModeValue("gray.100", "whiteAlpha.100");
  const edgeFade = useColorModeValue(
    "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.95))",
    "linear-gradient(to right, rgba(18,18,20,0), rgba(18,18,20,0.92))"
  );

  const handleAvatarClick = (e, notif) => {
    e.stopPropagation();
    e.preventDefault();
    const userId = notif?.user?.id;
    if (!userId) return;
    navigate(`/like/${userId}`);
  };

  const openLikesPage = (e) => {
    e?.stopPropagation?.();
    navigate("/likes");
  };

  return (
    <Box
      w="100%"
      h={LIKES_BAR_HEIGHT}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={shellBorder}
      overflow="hidden"
    >
      <Flex w="100%" h="100%">
        <Flex
          w={{ base: "84px", md: "92px" }}
          flexShrink={0}
          direction="column"
          align="center"
          justify="center"
          bg={countPanelBg}
          px={2}
          h="100%"
          zIndex={2}
          borderRightWidth="1px"
          borderColor={shellBorder}
        >
          <Text fontSize="3xl" fontWeight="bold" color={countColor} lineHeight={1} letterSpacing="-0.04em">
            {likeCount}
          </Text>
          <Text
            fontSize="10px"
            fontWeight="bold"
            color={labelColor}
            textTransform="uppercase"
            letterSpacing="0.1em"
            mt={1}
            lineHeight={1}
          >
            {t("nav.likes")}
          </Text>
        </Flex>

        <Box flex={1} position="relative" minW={0} h="100%">
          <Flex
            position="relative"
            align="center"
            gap={3}
            h="100%"
            pl={{ base: 3, md: 4 }}
            pr={{ base: 2, md: 3 }}
            bg={panelBg}
            filter={blurred ? `blur(${SECTION_BLUR})` : undefined}
            pointerEvents={blurred ? "none" : "auto"}
            userSelect={blurred ? "none" : undefined}
          >
            {likeCount > 0 ? (
              <Box position="relative" minW={0} flex={1} display="flex" alignItems="center">
                <Flex
                  align="center"
                  w="100%"
                  overflowX="auto"
                  overflowY="hidden"
                  pl={0.5}
                  pr={6}
                  sx={{
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {likesNotifications.map((notif, index) => (
                    <LikeAvatar
                      key={notif.likeNotificationId || notif.user?.id || index}
                      notif={notif}
                      index={index}
                      stackSize={likeCount}
                      onClick={(e) => handleAvatarClick(e, notif)}
                    />
                  ))}
                </Flex>
                <Box
                  pointerEvents="none"
                  position="absolute"
                  top={0}
                  right={0}
                  bottom={0}
                  w="28px"
                  backgroundImage={edgeFade}
                />
              </Box>
            ) : (
              <Box flex={1} />
            )}

            {!blurred && showClearHint && likeCount > 0 ? (
              <Text
                position="absolute"
                left={{ base: 3, md: 4 }}
                bottom="8px"
                fontSize="xs"
                color={muted}
                noOfLines={1}
                lineHeight="1.2"
                pointerEvents="none"
                display={{ base: "block", lg: "none" }}
              >
                {t("home.likesClearHint")}
              </Text>
            ) : null}

            {!blurred && likeCount > 0 ? (
              <Flex
                as="button"
                type="button"
                display={{ base: "inline-flex", lg: "none" }}
                align="center"
                gap={0.5}
                flexShrink={0}
                px={2.5}
                py={1.5}
                borderRadius="full"
                color={accent}
                fontSize="xs"
                fontWeight="semibold"
                onClick={openLikesPage}
                _hover={{ bg: viewAllHover }}
                transition="background 0.15s"
              >
                {t("home.seeAllLikes")}
                <Icon as={HiChevronRight} boxSize="14px" />
              </Flex>
            ) : null}
          </Flex>

          {blurred && likeCount > 0 ? (
            <Flex
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              bg={overlayBg}
              backdropFilter="blur(2px)"
              cursor="pointer"
              onClick={onUpgrade}
              zIndex={2}
            >
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={accent}
                display="inline-flex"
                alignItems="center"
                gap={1}
                whiteSpace="nowrap"
                lineHeight={1}
              >
                {t("messages.seeWhoLiked")}
                <Icon as={HiChevronRight} boxSize="14px" flexShrink={0} />
              </Text>
            </Flex>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
}
